import React from 'react';
import styled from 'styled-components';
import { colors, Heading } from '@telia/styleguide';
import { Offer, SalesPackage } from '../../types';
import { productInfoLookUp, offerInfoLookup, ribbonTextLookup } from '../../util/utils';
import { MediaQueries } from '../../util/MediaQueries';

interface BundleProps {
  salesPackage: SalesPackage;
  selected: boolean;
  hasInstallmentOffer: boolean;
  onSelect: () => void;
}

const getOfferTexts = (offers: Offer[]) => {
  return offers.map(offer => {
    return (
      <span style={{ fontSize: '1rem', fontFamily: 'Helvetica' }} key={offer.id}>
        {offerInfoLookup[offer.id] ?? offer.description}
      </span>
    );
  });
};

const getRibbonText = (offers: Offer[]) => {
  return offers.map(offer => {
    return (
      <span style={{ fontSize: '1rem', fontFamily: 'Helvetica' }} key={offer.id}>
        {ribbonTextLookup[offer.id] ?? 'TILBUD'}
      </span>
    );
  });
};

const SalesPackageView: React.FC<BundleProps> = ({ salesPackage, onSelect, selected, hasInstallmentOffer }) => {
  const hasOffer = salesPackage.offers.length > 0;
  const priceOffer = salesPackage.adjustedRecurringCharge < salesPackage.recurringCharge;
  const productInfo = productInfoLookUp[salesPackage.id];
  const productName = productInfo.name ?? salesPackage.name.toLowerCase();

  // Temp hack™ as offer price is now used as regular price...until they can fix it in icx
  const hideRibbon = true;

  return (
    <Container onClick={onSelect} onKeyDown={e => e.key === 'Enter' && onSelect()} isSelected={selected} tabIndex={0}>
      <RadioIcon>
        <CheckBoxSVG active={selected} />
      </RadioIcon>

      {hasOffer ? (
        <>
          <NameContainer>
            <PackageName tag="h3" size="s" text={productName} />
            {productInfo.extraName && <ExtraName tag="h3" size="s" text={productInfo.extraName} />}
          </NameContainer>
          <OfferContainer>
            {hideRibbon ? null : (
              <Ribbon>
                <RibbonText>{getRibbonText(salesPackage.offers)}</RibbonText>
              </Ribbon>
            )}

            {priceOffer ? (
              <PriceOfferContainer>
                <OfferText style={{ margin: 0 }}>
                  <strong>{getOfferTexts(salesPackage.offers)}</strong>
                </OfferText>
                <OfferText style={{ margin: 0 }}>
                  <strong>{salesPackage.adjustedRecurringCharge},-</strong> /md.
                </OfferText>
                <div>Deretter {salesPackage.recurringCharge},-</div>
              </PriceOfferContainer>
            ) : (
              <InstallOfferContainer>
                <OfferText style={{ width: '100%', color: '#990AE3' }}>
                  <strong>{getOfferTexts(salesPackage.offers)}</strong>
                </OfferText>
              </InstallOfferContainer>
            )}
          </OfferContainer>
          {!priceOffer && (
            <Price style={{ marginRight: '1.5rem' }}>
              <strong>{salesPackage.adjustedRecurringCharge},-</strong> /md.
            </Price>
          )}
        </>
      ) : (
        <>
          <PackageName tag="h3" size="s" text={productName} style={{ flex: '1' }} />

          <Price style={{ flex: '1', textAlign: 'end' }}>
            <strong>{salesPackage.adjustedRecurringCharge},-</strong> /md.
          </Price>
        </>
      )}
    </Container>
  );
};

export default SalesPackageView;

const CheckBoxSVG: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient spreadMethod="pad" y2="1" x2="1" y1="0" x1="0" id="svg_7">
        <stop offset="0.17188" stopColor="#00b6ca" />
        <stop offset="1" stopColor="#ffdc00" />
      </linearGradient>
    </defs>
    <g>
      <ellipse
        className="gradient"
        ry="8"
        rx="8"
        id="svg_1"
        cy="10"
        cx="10"
        strokeWidth="2"
        stroke="#757575"
        fill="#fff"
      />
      {active && <ellipse className="dot" ry="4" rx="4" id="svg_1" cy="10" cx="10" fill="#990ae3" />}
    </g>
  </svg>
);

interface ContainerProp {
  isSelected: boolean;
}

const Container = styled.div<ContainerProp>`
  border-radius: 4px;
  display: flex;
  margin: 1rem 0;
  padding: 1rem;
  max-width: 560px;
  height: 94px;
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: ${props => (props.isSelected ? colors.lightGrey : 'white')};
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.25);
  align-items: center;
  cursor: pointer;
  transition: border 150ms ease-out;
  ${MediaQueries.notDesktop} {
    height: 100%;
  }
`;
const NameContainer = styled.div`
  display: flex;
  flex-direction: row;

  ${MediaQueries.notDesktop} {
    margin: 0 0 0 0.5rem;
    max-height: 90px;
    flex-direction: column;
  }
`;
const OfferContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 1rem;
`;

const InstallOfferContainer = styled.div`
  margin-left: 25%;
  ${MediaQueries.medium} {
    margin-left: 10%;
  }
`;
const PriceOfferContainer = styled.div`
  padding-left: 4rem;
  ${MediaQueries.medium} {
    padding-left: 0;
  }
  ${MediaQueries.onlyMobile} {
    margin-left: -2rem;
  }
`;
const OfferText = styled.p`
  margin: 0 1rem 0 0;
  font-size: 1rem;
  strong {
    font-size: 1.375rem; // 22px
    font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;
    ${MediaQueries.onlyMobile} {
      font-size: 1rem;
    }
  }
  ${MediaQueries.medium} {
    margin-right: 0.5rem;
    width: 100%;
    text-align: left;
    line-height: 1;
    font-size: 0.8rem;
    padding: 0;
  }
`;
const RadioIcon = styled.div`
  margin: 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  ${MediaQueries.onlyMobile} {
    margin: 0 0.5rem 0 1rem;
  }
`;

const PackageName = styled(Heading)`
  text-transform: uppercase;
  font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;

  ${MediaQueries.notDesktop} {
    font-size: 1.2rem;
    margin: 0;
    width: 70%;
  }
`;

const ExtraName = styled(Heading)`
  text-transform: uppercase;
  font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;
  margin-left: 1rem;
  color: ${colors.corePurple};
  ${MediaQueries.notDesktop} {
    font-size: 1.2rem;
    width: 70px;
    margin: 0;
  }
`;

const Price = styled.p`
  margin: 0 1rem 0 0;
  font-size: 1rem;
  strong {
    font-size: 1.375rem; // 22px
    font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;
    ${MediaQueries.onlyMobile} {
      font-size: 1rem;
    }
  }
  ${MediaQueries.medium} {
    margin-right: 0.5rem;
  }
`;

const Ribbon = styled.div`
  width: 200px;
  top: 15px;
  right: -75px;
  left: auto;
  position: absolute;
  background-color: #990ae3;
  transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
  -webkit-transform: rotate(45deg);

  display: flex;
  justify-content: center;
`;

const RibbonText = styled.div`
  color: #ffffff;
  font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;
  font-size: 1rem;
  padding: 0.2rem;
`;
